import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Database,
  Search,
  Box,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Play,
  RotateCcw,
  Settings
} from 'lucide-react';
import { DataSourceConnector, DataSource } from './DataSourceConnector';
import { SchemaDiscovery, DiscoveredSchema } from './SchemaDiscovery';
import { CubeSchemaBuilder, CubeSchema } from './CubeSchemaBuilder';
import { useDataSourceSetup } from '@/hooks/analytics/useDataSourceSetup';
import { useToast } from '@/hooks/use-toast';

interface AnalyticsSetupWorkflowProps {
  onComplete?: (result: {
    dataSource: DataSource;
    discoveredSchema: DiscoveredSchema;
    cubeSchema: CubeSchema;
  }) => void;
  industryContext?: string;
  autoAdvance?: boolean;
}

export const AnalyticsSetupWorkflow: React.FC<AnalyticsSetupWorkflowProps> = ({
  onComplete,
  industryContext = 'general',
  autoAdvance = true
}) => {
  const [currentStep, setCurrentStep] = useState<'datasource' | 'discovery' | 'cube' | 'complete'>('datasource');
  const [dataSource, setDataSource] = useState<DataSource | null>(null);
  const [discoveredSchema, setDiscoveredSchema] = useState<DiscoveredSchema | null>(null);
  const [cubeSchema, setCubeSchema] = useState<CubeSchema | null>(null);

  const { toast } = useToast();
  const setupHook = useDataSourceSetup();

  const handleDataSourceSuccess = (newDataSource: DataSource) => {
    setDataSource(newDataSource);

    toast({
      title: "Data Source Connected",
      description: `Successfully connected to ${newDataSource.name}`,
    });

    if (autoAdvance) {
      setCurrentStep('discovery');
    }
  };

  const handleSchemaDiscovered = (schema: DiscoveredSchema) => {
    setDiscoveredSchema(schema);

    toast({
      title: "Schema Discovered",
      description: `Discovered ${schema.tables.length} tables with ${schema.metadata.overallConfidence}% confidence`,
    });

    if (autoAdvance) {
      setCurrentStep('cube');
    }
  };

  const handleCubeSchemaGenerated = (schema: CubeSchema) => {
    setCubeSchema(schema);

    toast({
      title: "Cube Schema Generated",
      description: `Generated ${schema.measures.length} measures and ${schema.dimensions.length} dimensions`,
    });

    if (autoAdvance) {
      setCurrentStep('complete');
    }

    if (onComplete && dataSource && discoveredSchema) {
      onComplete({
        dataSource,
        discoveredSchema,
        cubeSchema: schema
      });
    }
  };

  const handleDataSourceError = (error: Error) => {
    toast({
      title: "Connection Failed",
      description: error.message,
      variant: "destructive"
    });
  };

  const getStepStatus = (step: typeof currentStep) => {
    switch (step) {
      case 'datasource':
        return dataSource ? 'completed' : currentStep === 'datasource' ? 'active' : 'pending';
      case 'discovery':
        return discoveredSchema ? 'completed' : currentStep === 'discovery' ? 'active' : 'pending';
      case 'cube':
        return cubeSchema ? 'completed' : currentStep === 'cube' ? 'active' : 'pending';
      case 'complete':
        return cubeSchema ? 'completed' : 'pending';
    }
  };

  const getStepIcon = (step: typeof currentStep) => {
    const status = getStepStatus(step);

    if (status === 'completed') {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (status === 'active') {
      switch (step) {
        case 'datasource': return <Database className="h-5 w-5 text-blue-500" />;
        case 'discovery': return <Search className="h-5 w-5 text-blue-500" />;
        case 'cube': return <Box className="h-5 w-5 text-blue-500" />;
        case 'complete': return <CheckCircle className="h-5 w-5 text-blue-500" />;
      }
    } else {
      return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const getOverallProgress = () => {
    let progress = 0;
    if (dataSource) progress += 25;
    if (discoveredSchema) progress += 35;
    if (cubeSchema) progress += 40;
    return progress;
  };

  const canAdvanceToStep = (step: typeof currentStep) => {
    switch (step) {
      case 'datasource': return true;
      case 'discovery': return !!dataSource;
      case 'cube': return !!discoveredSchema;
      case 'complete': return !!cubeSchema;
    }
  };

  const resetWorkflow = () => {
    setCurrentStep('datasource');
    setDataSource(null);
    setDiscoveredSchema(null);
    setCubeSchema(null);
    setupHook.resetSetup();
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Progress Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Analytics Setup Workflow</span>
            <Badge variant="secondary" className="flex items-center gap-1">
              <Settings className="h-3 w-3" />
              {industryContext} context
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Overall Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Overall Progress</span>
                <span>{getOverallProgress()}%</span>
              </div>
              <Progress value={getOverallProgress()} className="h-2" />
            </div>

            {/* Step Indicators */}
            <div className="flex items-center justify-between">
              {[
                { key: 'datasource', label: 'Data Source', description: 'Connect to your data' },
                { key: 'discovery', label: 'Discovery', description: 'AI schema analysis' },
                { key: 'cube', label: 'Cube Schema', description: 'Generate analytics schema' },
                { key: 'complete', label: 'Complete', description: 'Ready for analytics' }
              ].map((step, index) => (
                <div key={step.key} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-12 w-12 rounded-full p-0 ${
                        getStepStatus(step.key as any) === 'active' ? 'ring-2 ring-blue-500' : ''
                      }`}
                      onClick={() => canAdvanceToStep(step.key as any) && setCurrentStep(step.key as any)}
                      disabled={!canAdvanceToStep(step.key as any)}
                    >
                      {getStepIcon(step.key as any)}
                    </Button>
                    <div className="text-center mt-2">
                      <div className="text-sm font-medium">{step.label}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">{step.description}</div>
                    </div>
                  </div>
                  {index < 3 && (
                    <ArrowRight className="h-4 w-4 text-gray-400 mx-4" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step Content */}
      <div className="space-y-6">
        {currentStep === 'datasource' && (
          <DataSourceConnector
            onConnectionSuccess={handleDataSourceSuccess}
            onConnectionError={handleDataSourceError}
            existingSources={dataSource ? [dataSource] : []}
          />
        )}

        {currentStep === 'discovery' && dataSource && (
          <SchemaDiscovery
            dataSource={dataSource}
            onSchemaDiscovered={handleSchemaDiscovered}
            industryContext={industryContext}
            aiEnhancement={true}
          />
        )}

        {currentStep === 'cube' && discoveredSchema && (
          <CubeSchemaBuilder
            discoveredSchema={discoveredSchema}
            onSchemaGenerated={handleCubeSchemaGenerated}
            industryContext={industryContext}
            optimizationLevel="balanced"
          />
        )}

        {currentStep === 'complete' && dataSource && discoveredSchema && cubeSchema && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Setup Complete!
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center space-y-4">
                <div className="text-lg">
                  Your analytics setup is now complete and ready to use.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Database className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                      <div className="font-medium">{dataSource.name}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Data Source Connected</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4 text-center">
                      <Search className="h-8 w-8 mx-auto mb-2 text-green-500" />
                      <div className="font-medium">{discoveredSchema.tables.length} Tables</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{discoveredSchema.metadata.overallConfidence}% AI Confidence</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4 text-center">
                      <Box className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                      <div className="font-medium">{cubeSchema.measures.length} Measures</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{cubeSchema.dimensions.length} Dimensions</div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <Button onClick={resetWorkflow} variant="outline">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Setup Another Source
                </Button>
                <Button onClick={() => window.location.href = '/analytics/dashboard'}>
                  <Play className="h-4 w-4 mr-2" />
                  Start Analyzing Data
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Navigation Controls */}
      {currentStep !== 'complete' && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Step {['datasource', 'discovery', 'cube', 'complete'].indexOf(currentStep) + 1} of 4
              </div>
              <div className="flex gap-2">
                {currentStep !== 'datasource' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const steps = ['datasource', 'discovery', 'cube', 'complete'] as const;
                      const currentIndex = steps.indexOf(currentStep);
                      if (currentIndex > 0) {
                        setCurrentStep(steps[currentIndex - 1]);
                      }
                    }}
                  >
                    Previous
                  </Button>
                )}

                {currentStep !== 'cube' && (
                  <Button
                    onClick={() => {
                      const steps = ['datasource', 'discovery', 'cube', 'complete'] as const;
                      const currentIndex = steps.indexOf(currentStep);
                      if (currentIndex < steps.length - 1 && canAdvanceToStep(steps[currentIndex + 1])) {
                        setCurrentStep(steps[currentIndex + 1]);
                      }
                    }}
                    disabled={(() => {
                      const steps = ['datasource', 'discovery', 'cube', 'complete'] as const;
                      const currentIndex = steps.indexOf(currentStep);
                      return currentIndex >= steps.length - 1 || !canAdvanceToStep(steps[currentIndex + 1]);
                    })()}
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};